/**
 * Zod schemas for the batch deal capture surface.
 *
 * A `DraftRow` represents one unsubmitted row in the editable table.
 * All fields are optional at the row level — validation runs per-row to drive
 * the status badge, and only fully-valid rows can be submitted.
 *
 * BigDecimal values (notional) travel as strings end-to-end. We deliberately
 * keep counterparty as a flat `{ id, name? }` ref (PartyRef) to match the
 * single-deal forms.
 */
import { z } from 'zod';
import {
  BigDecimalString,
  BuySell,
  CurrencyCode,
  IsoDateString,
  PartyRef,
} from './shared';

/**
 * Product types supported by the batch capture surface in PR 1.
 *
 * Intentionally narrow for the skeleton — additional product types will be
 * added as the per-product detail mapping is fleshed out in later PRs.
 */
export const BatchProductType = z.enum([
  'FX_SPOT',
  'FX_FORWARD',
  'IRS_VANILLA',
  'BOND',
  'DEPOSIT',
]);
export type BatchProductType = z.infer<typeof BatchProductType>;

/**
 * Strict per-row schema used to compute the row's validation status.
 * A row is "valid" (green badge) iff this schema parses successfully.
 */
export const DraftRowStrictSchema = z.object({
  productType: BatchProductType,
  side: BuySell,
  counterparty: PartyRef,
  notional: BigDecimalString,
  currency: CurrencyCode,
  effectiveDate: IsoDateString,
  maturityDate: IsoDateString,
});

export type DraftRowStrict = z.infer<typeof DraftRowStrictSchema>;

/**
 * Lenient row schema used for in-progress editing — every field is optional.
 * This is the shape held in component state; strict validation runs as a
 * separate `safeParse` against `DraftRowStrictSchema`.
 */
export const DraftRowSchema = z.object({
  productType: BatchProductType.optional(),
  side: BuySell.optional(),
  counterparty: PartyRef.optional(),
  notional: z.string().optional(),
  currency: z.string().optional(),
  effectiveDate: z.string().optional(),
  maturityDate: z.string().optional(),
});

export type DraftRow = z.infer<typeof DraftRowSchema>;

/**
 * Per-row submission lifecycle. Drives the status-cell rendering during and
 * after a batch submit, and the post-submit table cleanup (successful rows
 * are removed; failed rows stay in place with their `message` annotated).
 */
export type RowSubmissionState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'submitted'; dealId: string }
  | { kind: 'error'; message: string };

/**
 * A row as it lives in the table — wraps the draft data with a client-side
 * id and its current submission lifecycle (defaults to `{ kind: 'idle' }`).
 */
export type BatchRow = {
  /** Client-side row id (crypto.randomUUID()). */
  id: string;
  data: DraftRow;
  submission?: RowSubmissionState;
};

/**
 * Payload shape accepted by the surface from chat / direct nav.
 * Agents can prefill rows but the user must click submit — no auto-submit.
 */
export const BatchDealCapturePayloadSchema = z.object({
  rows: z.array(DraftRowSchema).optional(),
});

export type BatchDealCapturePayload = z.infer<typeof BatchDealCapturePayloadSchema>;
